import { Component, inject, output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import {
  CancellationCancelReason,
  getCancellationCancelReasonLabel,
} from 'src/app/enums/cancellation/cancellation-cancel-reason.enum';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';

/**
 * « Annuler la résiliation »: the request is called off wherever it had got to,
 * and stays readable with the reason it died — unlike a deletion, which takes
 * back a request that should never have existed. The reason is the whole
 * dialog: confirming without one says so rather than calling anything off.
 */
@Component({
  selector: 'app-cancellation-cancel-modal',
  standalone: true,
  imports: [ReactiveFormsModule, ActionModalComponent, IconsModule],
  templateUrl: './cancellation-cancel-modal.component.html',
  styleUrl: './cancellation-cancel-modal.component.css',
})
export class CancellationCancelModalComponent {
  private readonly cancellationService = inject(CancellationService);

  private readonly toastr = inject(ToastrService);

  /** The cancellation called off: the refreshed one. */
  readonly changed = output<CancellationFlowDetailsResponse>();

  @ViewChild('actionModal')
  private actionModal?: ActionModalComponent;

  private cancellationId: string | null = null;

  /** Offered in the enum's own order: likeliest first. */
  protected readonly reasons = Object.values(CancellationCancelReason);

  protected readonly getCancellationCancelReasonLabel =
    getCancellationCancelReasonLabel;

  protected readonly reason = new FormControl<CancellationCancelReason | null>(
    null
  );

  protected submitted = false;

  /**
   * « Annuler la résiliation » was clicked this opening — not `touched`: the
   * dialog focuses the first radio, so any click elsewhere would blur it.
   */
  protected confirmTried = false;

  openModal(cancellationId: string): void {
    this.cancellationId = cancellationId;
    this.submitted = false;
    this.confirmTried = false;
    this.reason.reset();
    this.actionModal?.openModal();
  }

  closeModal(): void {
    this.actionModal?.closeModal();
  }

  protected confirm(): void {
    this.confirmTried = true;
    const id = this.cancellationId;
    const reason = this.reason.value;
    if (!id || !reason || this.submitted) return;
    this.submitted = true;
    this.cancellationService.cancel(id, { reason }).subscribe({
      next: (res) => {
        this.submitted = false;
        this.toastr.success($localize`Résiliation annulée`);
        this.changed.emit(res.data);
        this.actionModal?.closeModal();
      },
      error: (err) => {
        this.submitted = false;
        this.toastr.error(
          getApiErrorMessage(
            err,
            $localize`Une erreur est survenue. Veuillez réessayer`
          )
        );
      },
    });
  }
}
